import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Building, DollarSign, LogOut, ClipboardList, Handshake, TrendingUp, Clock, CalendarDays, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchStats(token);
  }, [navigate]);

  const fetchStats = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [volRes, partRes, evRes, tdRes] = await Promise.all([
        fetch(`${API}/api/volunteers`, { headers }),
        fetch(`${API}/api/partnerships`, { headers }),
        fetch(`${API}/api/events`),
        fetch(`${API}/api/turkey-drive/submissions`, { headers })
      ]);
      if (volRes.status === 401 || partRes.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }
      const volData = await volRes.json();
      const partData = await partRes.json();
      const evData = await evRes.json();
      const tdData = tdRes.ok ? await tdRes.json() : { submissions: [] };
      const volunteers = volData.applications || [];
      const partnerships = partData.inquiries || [];
      const events = evData.events || [];
      const turkeyDrive = tdData.submissions || [];
      setStats({
        totalVolunteers: volunteers.length,
        totalPartnerships: partnerships.length,
        totalEvents: events.filter(e => e.status === 'active').length,
        totalTurkeyDrive: turkeyDrive.length,
        recentVolunteers: volunteers.slice(-3).reverse(),
        recentPartnerships: partnerships.slice(-3).reverse(),
      });
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gray-50">
        <p className="text-lg text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-title">Admin Dashboard</h1>
            <p className="text-gray-500 mt-1">WHY ME Foundation Overview</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-300 hover:bg-red-50" data-testid="dashboard-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <Card className="p-6 border-l-4 border-l-[#E6B933]" data-testid="stat-volunteers">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Volunteer Applications</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalVolunteers || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#E6B933]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#E6B933]" />
              </div>
            </div>
            <Link to="/admin/volunteers" className="text-sm text-[#E6B933] hover:underline mt-3 inline-flex items-center gap-1">
              View all <TrendingUp className="w-3 h-3" />
            </Link>
          </Card>

          <Card className="p-6 border-l-4 border-l-blue-500" data-testid="stat-partnerships">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Partnership Inquiries</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalPartnerships || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-500" />
              </div>
            </div>
            <Link to="/admin/partnerships" className="text-sm text-blue-500 hover:underline mt-3 inline-flex items-center gap-1">
              View all <TrendingUp className="w-3 h-3" />
            </Link>
          </Card>

          <Card className="p-6 border-l-4 border-l-green-500" data-testid="stat-total">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Active Events</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalEvents || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <CalendarDays className="w-6 h-6 text-green-500" />
              </div>
            </div>
            <Link to="/admin/events" className="text-sm text-green-500 hover:underline mt-3 inline-flex items-center gap-1">
              Manage <TrendingUp className="w-3 h-3" />
            </Link>
          </Card>

          <Card className="p-6 border-l-4 border-l-orange-500" data-testid="stat-turkey-drive">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Turkey Drive Sponsors</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalTurkeyDrive || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <Heart className="w-6 h-6 text-orange-500" />
              </div>
            </div>
            <Link to="/admin/turkey-drive" className="text-sm text-orange-500 hover:underline mt-3 inline-flex items-center gap-1">
              View all <TrendingUp className="w-3 h-3" />
            </Link>
          </Card>
        </div>
        <div className="grid sm:grid-cols-3 gap-6 mb-10">
          <Link to="/admin/volunteers">
            <Card className="p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group border-2 border-transparent hover:border-[#E6B933]/30" data-testid="nav-card-volunteers">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#E6B933]/10 flex items-center justify-center group-hover:bg-[#E6B933]/20 transition-colors">
                  <Users className="w-7 h-7 text-[#E6B933]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Volunteers</h3>
                  <p className="text-sm text-gray-500">Review volunteer signups</p>
                </div>
              </div>
            </Card>
          </Link>
          <Link to="/admin/partnerships">
            <Card className="p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group border-2 border-transparent hover:border-blue-300" data-testid="nav-card-partnerships">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Handshake className="w-7 h-7 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Partnerships</h3>
                  <p className="text-sm text-gray-500">Review partnership requests</p>
                </div>
              </div>
            </Card>
          </Link>
          <Link to="/admin/events">
            <Card className="p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group border-2 border-transparent hover:border-green-300" data-testid="nav-card-events">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                  <CalendarDays className="w-7 h-7 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Events</h3>
                  <p className="text-sm text-gray-500">Create and manage events</p>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6" data-testid="recent-volunteers">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#E6B933]" /> Recent Volunteers
            </h3>
            {stats?.recentVolunteers?.length > 0 ? (
              <div className="space-y-3">
                {stats.recentVolunteers.map((v, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{v.name}</p>
                      <p className="text-xs text-gray-500">{v.interest}</p>
                    </div>
                    <span className="text-xs text-gray-400">{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No volunteer applications yet.</p>
            )}
          </Card>

          <Card className="p-6" data-testid="recent-partnerships">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" /> Recent Partnerships
            </h3>
            {stats?.recentPartnerships?.length > 0 ? (
              <div className="space-y-3">
                {stats.recentPartnerships.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{p.organization}</p>
                      <p className="text-xs text-gray-500">{p.partnership_type}</p>
                    </div>
                    <span className="text-xs text-gray-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No partnership inquiries yet.</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
